import React, { useEffect, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import premierLogo from "../../../assessts/premier-logo.png";
import "./Domain.css";

const toViewSubscription = (item) => ({
  id: item._id || item.id,
  subscriber: item.subscriber || "-",
  billToAddress: item.billToAddress || "",
  plan: item.plan || "-",
  billingCycle: item.billingCycle || "-",
  paymentMethod: item.paymentMethod || "-",
  amount: Number(item.amount || 0),
  paidAmount: Number(item.paidAmount || 0),
  createdDate: item.createdDate || null,
  expiringOn: item.expiringOn || null,
  status: ["Paid", "Unpaid", "Partial"].includes(item.status) ? item.status : "Unpaid",
  signatureData: item.signatureData || "",
  signatureMimeType: item.signatureMimeType || "",
  currency: item.currency || "INR",
});

function Subscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [payNow, setPayNow] = useState({});
  const [serviceSubscriptions, setServiceSubscriptions] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [isUploadingSignature, setIsUploadingSignature] = useState(false);
  const [isRemovingSignature, setIsRemovingSignature] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchSubscriptions = async () => {
    try {
      setIsLoading(true);
      setError("");
      const response = await axiosInstance.get("/api/subscriptions");
      const list = Array.isArray(response.data) ? response.data : [];
      const mapped = list.map(toViewSubscription);
      setSubscriptions(mapped);
      const defaultPayNow = {};
      mapped.forEach((item) => {
        defaultPayNow[item.id] = "";
      });
      setPayNow(defaultPayNow);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to fetch subscriptions");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchServiceSubscriptions = async () => {
    try {
      const response = await axiosInstance.get("/api/serviceSubscriptions");
      setServiceSubscriptions(Array.isArray(response.data) ? response.data : []);
    } catch {
      setServiceSubscriptions([]);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
    fetchServiceSubscriptions();
  }, []);

  const handleStatusChange = async (id, status) => {
    try {
      setError("");
      await axiosInstance.put(`/api/subscriptions/${id}/status`, { status });
      setSubscriptions((prev) =>
        prev.map((item) => (item.id === id ? { ...item, status } : item))
      );
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update status");
    }
  };

  const handleDeleteSubscription = async (id) => {
    if (!window.confirm("Delete this subscription?")) return;

    try {
      setError("");
      await axiosInstance.delete(`/api/subscriptions/${id}`);
      setSubscriptions((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to delete subscription");
    }
  };

  const handleAddPayment = async (item) => {
    if (item.status === "Paid") {
      alert("This subscription is already fully paid.");
      return;
    }

    const amount = Number(payNow[item.id]) || 0;
    if (amount <= 0) return alert("Enter valid amount");

    try {
      setError("");
      const response = await axiosInstance.post(`/api/subscriptions/${item.id}/addPayment`, {
        amount,
      });
      const updated = toViewSubscription(response.data || {});
      setSubscriptions((prev) => prev.map((sub) => (sub.id === updated.id ? updated : sub)));
      setPayNow((prev) => ({ ...prev, [item.id]: "" }));
      await fetchSubscriptions();
      alert("Payment added successfully.");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to add payment");
    }
  };

  const handleDeleteServiceSubscription = async (id) => {
    if (!window.confirm("Delete this saved service subscription?")) return;
    try {
      await axiosInstance.delete(`/api/serviceSubscriptions/${id}`);
      setServiceSubscriptions((prev) => prev.filter((item) => item._id !== id));
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to delete saved service subscription");
    }
  };

  const handleSendTestReminder = async (id) => {
    try {
      await axiosInstance.post(`/api/serviceSubscriptions/${id}/test-reminder`);
      alert("Test reminder sent.");
    } catch (err) {
      const backendMessage =
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data : "") ||
        err?.message;
      alert(backendMessage || "Failed to send test reminder");
    }
  };

  const handleUpdateCurrency = async (id, currency) => {
    try {
      const response = await axiosInstance.put(`/api/subscriptions/${id}/currency`, { currency });
      const updated = toViewSubscription(response.data || {});
      setPreviewItem(updated);
      setSubscriptions((prev) =>
        prev.map((item) => (item.id === id ? { ...item, currency: updated.currency } : item))
      );
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to update currency");
    }
  };

  const handleUpdateBillToAddress = async () => {
    if (!previewItem?.id) return;

    const currentAddress = previewItem.billToAddress || "";
    const address = window.prompt("Enter Bill To Address", currentAddress);
    if (address === null) return;

    const trimmedAddress = String(address).trim();
    if (!trimmedAddress) {
      alert("Address cannot be empty.");
      return;
    }

    try {
      const response = await axiosInstance.put(`/api/subscriptions/${previewItem.id}/address`, {
        billToAddress: trimmedAddress,
      });
      const updated = toViewSubscription(response.data || {});
      setPreviewItem(updated);
      setSubscriptions((prev) =>
        prev.map((item) =>
          item.id === updated.id
            ? { ...item, billToAddress: updated.billToAddress }
            : item
        )
      );
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to update Bill To address");
    }
  };

  const handleDownloadSubscription = (item) => {
    const lines = [
      `Subscriber: ${item.subscriber}`,
      `Bill To Address: ${item.billToAddress || "-"}`,
      `Plan Name: ${item.plan}`,
      `Billing Cycle: ${item.billingCycle}`,
      `Payment Method: ${item.paymentMethod}`,
      `Amount: ${item.amount}`,
      `Created Date: ${formatDate(item.createdDate)}`,
      `Expiring On: ${formatDate(item.expiringOn)}`,
      `Status: ${item.status}`,
    ];

    const fileContent = lines.join("\n");
    const blob = new Blob([fileContent], { type: "text/plain;charset=utf-8" });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safeName = String(item.subscriber || "subscription")
      .trim()
      .replace(/[^a-z0-9-_]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase();

    link.href = objectUrl;
    link.download = `${safeName || "subscription"}-details.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  const handlePreviewSubscription = async (id) => {
    try {
      setIsPreviewLoading(true);
      setError("");
      const response = await axiosInstance.get(`/api/subscriptions/${id}`);
      setPreviewItem(toViewSubscription(response.data || {}));
      setSignatureFile(null);
      setShowPreview(true);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to preview subscription");
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-IN");
  };

  const formatCurrency = (value, currencyCode = "INR") => {
    const amount = Number(value || 0);
    const sym = { INR: "₹", USD: "$", EUR: "€", GBP: "£", CAD: "CA$" }[currencyCode] || "₹";
    return `${sym}${amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getBalanceAmount = (item) => {
    const total = Number(item?.amount || 0);
    const paid = Number(item?.paidAmount || 0);
    return Math.max(total - paid, 0);
  };

  const handleSignatureFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setSignatureFile(null);
      return;
    }

    const validTypes = ["image/jpeg", "image/png"];
    if (!validTypes.includes(file.type)) {
      alert("Please upload only JPG or PNG image.");
      e.target.value = "";
      setSignatureFile(null);
      return;
    }

    if (file.size > 200 * 1024) {
      alert("Signature image must be 200KB or less.");
      e.target.value = "";
      setSignatureFile(null);
      return;
    }

    setSignatureFile(file);
  };

  const handleUploadSignature = async () => {
    if (!previewItem?.id) return;
    if (!signatureFile) {
      alert("Please choose a signature image first.");
      return;
    }

    try {
      setIsUploadingSignature(true);
      const form = new FormData();
      form.append("signature", signatureFile);

      const response = await axiosInstance.post(`/api/subscriptions/${previewItem.id}/signature`, form);
      const updated = toViewSubscription(response.data || {});
      setPreviewItem(updated);
      setSubscriptions((prev) => prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)));
      setSignatureFile(null);
    } catch (err) {
      const backendMessage =
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data : "") ||
        err?.message;
      alert(backendMessage || "Failed to upload signature");
    } finally {
      setIsUploadingSignature(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!previewItem?.id) return;
    try {
      setIsDownloadingPdf(true);
      const response = await axiosInstance.get(`/api/subscriptions/${previewItem.id}/pdf`, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeName = String(previewItem.subscriber || "subscription")
        .trim()
        .replace(/[^a-z0-9-_]+/gi, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase();
      link.href = objectUrl;
      link.download = `${safeName || "subscription"}-invoice.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to download PDF");
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handleRemoveSignature = async () => {
    if (!previewItem?.id) return;
    if (!window.confirm("Remove signature from this invoice?")) return;

    try {
      setIsRemovingSignature(true);
      let response;
      try {
        const form = new FormData();
        form.append("removeSignature", "true");
        response = await axiosInstance.put(`/api/subscriptions/${previewItem.id}/signature`, form);
      } catch (putError) {
        response = await axiosInstance.delete(`/api/subscriptions/${previewItem.id}/signature`);
      }

      const updated = toViewSubscription(response.data || {});
      setPreviewItem(updated);
      setSubscriptions((prev) => prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)));
      setSignatureFile(null);
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to remove signature");
    } finally {
      setIsRemovingSignature(false);
    }
  };

  return (
    <div className="domain-page">
      <div className="domain-hero">
        <div>
          <p className="domain-subtitle">Admin Domain Portal</p>
          <h2 className="domain-title">Subscriptions</h2>
          <p className="domain-caption">Track and manage client subscription plans and renewals</p>
        </div>
      </div>

      {error && <div className="domain-error">{error}</div>}

      <div className="domain-table-wrap">
        <div className="domain-table-scroll">
          <table className="domain-table">
            <thead>
              <tr>
                <th>Subscriber</th>
                <th>Plan Name</th>
                <th>Billing Cycle</th>
                <th>Payment Method</th>
                <th>Amount</th>
                <th>Created Date</th>
                <th>Expiring On</th>
                <th>Pay Now</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {!isLoading && subscriptions.length === 0 && (
                <tr>
                  <td colSpan={10} style={{ padding: "40px 10px", textAlign: "center", color: "#64748b" }}>
                    No subscriptions found in the database.
                  </td>
                </tr>
              )}

              {subscriptions.map((item) => (
                <tr key={item.id}>
                  <td>{item.subscriber}</td>
                  <td>{item.plan}</td>
                  <td>{item.billingCycle}</td>
                  <td>{item.paymentMethod}</td>
                  <td>{formatCurrency(item.amount, item.currency)}</td>
                  <td>{formatDate(item.createdDate)}</td>
                  <td>{formatDate(item.expiringOn)}</td>
                  <td>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <input
                        value={payNow[item.id] || ""}
                        onChange={(e) => setPayNow((prev) => ({ ...prev, [item.id]: e.target.value }))}
                        placeholder="Amount"
                        style={{
                          width: "110px",
                          border: "1px solid #9fc3e4",
                          borderRadius: "10px",
                          padding: "8px 10px",
                          outline: "none",
                        }}
                      />
                      <button
                        className="domain-btn domain-btn-view"
                        type="button"
                        onClick={() => handleAddPayment(item)}
                      >
                        Add
                      </button>
                    </div>
                  </td>
                  <td>
                    <select
                      className="domain-select"
                      value={item.status}
                      onChange={(e) => handleStatusChange(item.id, e.target.value)}
                    >
                      <option value="Paid">Paid</option>
                      <option value="Partial">Partial</option>
                      <option value="Unpaid">Unpaid</option>
                    </select>
                  </td>
                  <td>
                    <div className="domain-actions">
                      <button
                        className="domain-btn domain-btn-view"
                        type="button"
                        disabled={isPreviewLoading}
                        onClick={() => handlePreviewSubscription(item.id)}
                      >
                        Preview
                      </button>
                      <button
                        className="domain-btn"
                        type="button"
                        onClick={() => handleDownloadSubscription(item)}
                      >
                        Download
                      </button>
                      <button
                        className="domain-btn domain-btn-edit"
                        type="button"
                        disabled={isPreviewLoading}
                        onClick={() => handlePreviewSubscription(item.id)}
                      >
                        Edit
                      </button>
                      <button
                        className="domain-btn domain-btn-delete"
                        type="button"
                        onClick={() => handleDeleteSubscription(item.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showPreview && previewItem && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(5, 30, 53, 0.65)",
            zIndex: 1100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
          onClick={() => setShowPreview(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(900px, 100%)",
              maxHeight: "90vh",
              overflowY: "auto",
              background: "linear-gradient(180deg, #ffffff, #f7fbff)",
              border: "1px solid #b9cee5",
              borderRadius: "16px",
              padding: "24px",
              color: "#111827",
              boxShadow: "0 24px 44px rgba(7, 46, 80, 0.24)",
            }}
          >
            <h2
              style={{
                margin: "0 0 8px 0",
                textAlign: "center",
                color: "#1976be",
                letterSpacing: "0.6px",
                fontWeight: 700,
              }}
            >
              INVOICE
            </h2>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 220px", gap: "12px", alignItems: "start" }}>
              <div style={{ fontSize: "15px", lineHeight: 1.5 }}>
                <div style={{ color: "#1976be", fontWeight: 700 }}>Premier Webtech</div>
                <div>India</div>
                <div>Email: support@premierwebtech.com</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <img src={premierLogo} alt="Premier Webtech Logo" style={{ width: "115px", height: "auto", objectFit: "contain" }} />
              </div>
            </div>

            <div style={{ marginTop: "8px", borderTop: "2px solid #bdd6eb", paddingTop: "10px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: "12px" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
                    <div style={{ color: "#1976be", fontWeight: 700 }}>Bill To</div>
                    <button
                      type="button"
                      onClick={handleUpdateBillToAddress}
                      style={{
                        border: "1px solid #b9cde3",
                        background: "#ffffff",
                        color: "#0b3f6b",
                        borderRadius: "8px",
                        padding: "2px 8px",
                        fontSize: "12px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Add Address
                    </button>
                    <select
                      value={previewItem.currency || "INR"}
                      onChange={(e) => handleUpdateCurrency(previewItem.id, e.target.value)}
                      style={{
                        border: "1px solid #b9cde3",
                        background: "#ffffff",
                        color: "#0b3f6b",
                        borderRadius: "8px",
                        padding: "2px 8px",
                        fontSize: "12px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      <option value="INR">INR (₹)</option>
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                      <option value="GBP">GBP (£)</option>
                      <option value="CAD">CAD (CA$)</option>
                    </select>
                  </div>
                  <div>{previewItem.subscriber || "-"}</div>
                  <div style={{ marginTop: "6px", whiteSpace: "pre-line", color: "#334155" }}>
                    {previewItem.billToAddress || "No address added"}
                  </div>
                </div>
                <div style={{ justifySelf: "end", width: "100%" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", rowGap: "4px" }}>
                    <strong>Invoice No :</strong>
                    <strong style={{ textAlign: "right" }}>INV{String(previewItem.id || "").slice(-4).toUpperCase()}</strong>
                    <span>Invoice Date :</span>
                    <span style={{ textAlign: "right" }}>{formatDate(previewItem.createdDate)}</span>
                    <span>Due Date :</span>
                    <span style={{ textAlign: "right" }}>{formatDate(previewItem.expiringOn)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: "14px", border: "1px solid #cbd5e1" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#1976be", color: "#ffffff" }}>
                    <th style={{ padding: "8px 10px", textAlign: "left", width: "50px" }}>Sl</th>
                    <th style={{ padding: "8px 10px", textAlign: "left" }}>Description</th>
                    <th style={{ padding: "8px 10px", textAlign: "right", width: "90px" }}>Qty</th>
                    <th style={{ padding: "8px 10px", textAlign: "right", width: "130px" }}>Rate</th>
                    <th style={{ padding: "8px 10px", textAlign: "right", width: "130px" }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ background: "#ffffff" }}>
                    <td style={{ padding: "8px 10px", borderTop: "1px solid #e5e7eb" }}>1</td>
                    <td style={{ padding: "8px 10px", borderTop: "1px solid #e5e7eb" }}>
                      {previewItem.plan} Subscription ({previewItem.billingCycle})
                    </td>
                    <td style={{ padding: "8px 10px", borderTop: "1px solid #e5e7eb", textAlign: "right" }}>1</td>
                    <td style={{ padding: "8px 10px", borderTop: "1px solid #e5e7eb", textAlign: "right" }}>{formatCurrency(previewItem.amount, previewItem.currency)}</td>
                    <td style={{ padding: "8px 10px", borderTop: "1px solid #e5e7eb", textAlign: "right" }}>{formatCurrency(previewItem.amount, previewItem.currency)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: "14px", display: "grid", gridTemplateColumns: "1fr 280px", gap: "18px" }}>
              <div>
                <div style={{ color: "#1976be", fontWeight: 700, marginBottom: "6px" }}>Payment Instructions</div>
                <div>Pay via {previewItem.paymentMethod}</div>
                <div style={{ marginTop: "6px" }}>Amount Due: {formatCurrency(getBalanceAmount(previewItem), previewItem.currency)}</div>
              </div>
              <div style={{ borderTop: "2px solid #bdd6eb", borderBottom: "2px solid #bdd6eb", padding: "8px 0" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                  <span>Sub Total</span>
                  <strong>{formatCurrency(previewItem.amount, previewItem.currency)}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                  <span>Paid</span>
                  <strong>{formatCurrency(previewItem.paidAmount, previewItem.currency)}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                  <span>Tax</span>
                  <strong>{formatCurrency(0, previewItem.currency)}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
                  <span>Balance</span>
                  <span>{formatCurrency(getBalanceAmount(previewItem), previewItem.currency)}</span>
                </div>
              </div>
            </div>

            <div style={{ marginTop: "16px", display: "flex", justifyContent: "flex-end" }}>
              <div style={{ width: "220px", textAlign: "center", border: "1px solid #b9cee5", borderRadius: "8px", padding: "8px 10px" }}>
                {previewItem.signatureData && previewItem.signatureMimeType ? (
                  <img
                    src={`data:${previewItem.signatureMimeType};base64,${previewItem.signatureData}`}
                    alt="Authorized Signature"
                    style={{
                      maxWidth: "100%",
                      height: "42px",
                      objectFit: "contain",
                      marginBottom: "6px",
                    }}
                  />
                ) : (
                  <div style={{ borderTop: "1px solid #94a3b8", marginBottom: "8px", minHeight: "34px" }}></div>
                )}
                <div style={{ fontSize: "13px", fontWeight: 600 }}>Authorized Signatory</div>
              </div>
            </div>

            <div style={{ marginTop: "14px", display: "grid", gridTemplateColumns: "1fr auto", gap: "10px", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: "13px", color: "#0b3f6b", fontWeight: 700, marginBottom: "6px" }}>Upload Signature (jpg/png, max 200KB)</div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                    onChange={handleSignatureFileChange}
                    style={{ fontSize: "13px" }}
                  />
                  <button
                    type="button"
                    onClick={handleUploadSignature}
                    disabled={isUploadingSignature}
                    style={{
                      border: "1px solid #0b4a7f",
                      background: "linear-gradient(180deg, #0f5a97, #084475)",
                      color: "#ffffff",
                      borderRadius: "8px",
                      padding: "7px 12px",
                      fontWeight: 600,
                      cursor: isUploadingSignature ? "not-allowed" : "pointer",
                      opacity: isUploadingSignature ? 0.7 : 1,
                    }}
                  >
                    {isUploadingSignature ? "Uploading..." : "Upload"}
                  </button>
                  {previewItem.signatureData && previewItem.signatureMimeType ? (
                    <button
                      type="button"
                      onClick={handleRemoveSignature}
                      disabled={isRemovingSignature}
                      style={{
                        border: "1px solid #b42318",
                        background: "#ffffff",
                        color: "#b42318",
                        borderRadius: "8px",
                        padding: "7px 12px",
                        fontWeight: 600,
                        cursor: isRemovingSignature ? "not-allowed" : "pointer",
                        opacity: isRemovingSignature ? 0.7 : 1,
                      }}
                    >
                      {isRemovingSignature ? "Removing..." : "Remove Signature"}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>

            <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button
                type="button"
                onClick={handleDownloadPdf}
                disabled={isDownloadingPdf}
                style={{
                  border: "1px solid #0b4a7f",
                  background: "linear-gradient(180deg, #0f5a97, #084475)",
                  color: "#ffffff",
                  borderRadius: "12px",
                  padding: "8px 14px",
                  fontWeight: 600,
                  cursor: isDownloadingPdf ? "not-allowed" : "pointer",
                  opacity: isDownloadingPdf ? 0.7 : 1,
                }}
              >
                {isDownloadingPdf ? "Downloading..." : "Download PDF"}
              </button>
              <button
                type="button"
                onClick={() => setShowPreview(false)}
                style={{
                  border: "1px solid #b9cde3",
                  background: "#ffffff",
                  color: "#0b3f6b",
                  borderRadius: "12px",
                  padding: "8px 14px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Subscriptions;
